import { measureTextWidth } from '../../utils/measureTextWidth';

/**
 * @param {Array<{folderShareId, folderId}>} shares
 * @param {Object} folderRootMetadata — { [folderShareId]: { id, name, parentId, path, ... } }
 * @param {Object} subfolders — { [folderId]: FolderApiResponse[] }
 * @param {string[]} [inaccessibleFolderIds=[]] — ids of revoked/deleted folders; excluded as
 *   nodes and ignored when deduping, so still-shared descendants of an inaccessible ancestor
 *   surface as their own top-level roots.
 * @returns {TreeViewNode[]}
 */
export function buildTreeNodes(shares, folderRootMetadata, subfolders, inaccessibleFolderIds = []) {
  const inaccessibleFolderIdSet = new Set(inaccessibleFolderIds);

  // Build a lookup of all root folder ids that are shared.
  // A share is redundant if its root folder's parentId points to another shared folder —
  // i.e. it is a direct child of another share. We use the parentId from the folder
  // metadata (now available directly from the API response) for accurate detection
  // without requiring the subfolders cache to be populated first.
  const sharedFolderIds = new Set(
    shares
      .map(({ folderShareId }) => folderRootMetadata[folderShareId]?.id)
      .filter((id) => id && !inaccessibleFolderIdSet.has(id))
  );

  const dedupedShares = shares.filter(({ folderShareId }) => {
    const root = folderRootMetadata[folderShareId];
    if (!root) return false;
    // If this folder's parent is also a shared root, suppress it — it will appear
    // naturally when its parent is expanded.
    // Walk up the path array to handle ancestry deeper than 1 level.
    const path = root.path ?? [];
    for (const ancestor of path) {
      if (ancestor.id !== root.id && sharedFolderIds.has(ancestor.id)) {
        return false;
      }
    }
    return true;
  });

  // Deduplicate by root folder id (same folder shared via multiple assignments).
  const seenRootIds = new Set();
  return dedupedShares
    .map(({ folderShareId }) => {
      const rootFolder = folderRootMetadata[folderShareId];
      if (!rootFolder || !rootFolder.id) return null;
      if (seenRootIds.has(rootFolder.id)) return null;
      if (inaccessibleFolderIdSet.has(rootFolder.id)) return null;
      seenRootIds.add(rootFolder.id);
      return buildNode(rootFolder, subfolders, inaccessibleFolderIdSet);
    })
    .filter(Boolean);
}

/**
 * Effective max label width in the TreeView's indented rows after icons, chevron,
 * padding and gaps — mirrors TREE_LABEL_MAX_WIDTH_PX in the ui workspace's
 * useFolderTree.ts, so tooltip-on-truncation behavior is identical between apps.
 */
const TREE_LABEL_MAX_WIDTH_PX = 200;

export function buildNode(folder, subfolders, inaccessibleFolderIdSet = new Set()) {
  const cachedChildren = Object.prototype.hasOwnProperty.call(subfolders, folder.id)
    ? subfolders[folder.id]
    : undefined; // undefined = not yet fetched

  const visibleCachedChildren = cachedChildren?.filter((child) => !inaccessibleFolderIdSet.has(child.id));

  // Only attach children array when we have fetched data.
  // Do NOT attach an empty children array for a folder that has hasChildren=true
  // but hasn't been expanded yet — that would remove the expand chevron.
  const children =
    visibleCachedChildren !== undefined
      ? visibleCachedChildren.map((child) => buildNode(child, subfolders, inaccessibleFolderIdSet))
      : undefined;

  // hasChildren truth order:
  //   1. If we've fetched children, derive from the visible (accessible) count (authoritative).
  //   2. If not yet fetched, fall back to the API-provided hasChildren flag.
  //   3. If neither is available, default false (no chevron until we know).
  const hasChildren =
    visibleCachedChildren !== undefined ? visibleCachedChildren.length > 0 : folder.hasChildren ?? false;

  return {
    id: folder.id,
    name: folder.name,
    // ZUI's TreeView only wraps the label in a Tooltip (and makes it focusable) when
    // `title` is set — omitting it for short names avoids an always-focusable node
    // label that would otherwise show its tooltip immediately on keyboard focus.
    title: measureTextWidth(folder.name) > TREE_LABEL_MAX_WIDTH_PX ? folder.name : undefined,
    hasChildren,
    children,
  };
}

/**
 * Given a folderId that was just expanded, find the share it belongs to.
 * Returns the matching share object { folderShareId, folderId, ... } or null.
 */
export function findShareForFolder(folderId, shares, subfolders) {
  // 1. Root-level check
  for (const share of shares) {
    if (share.folderId === folderId) return share;
  }

  // 2. Walk loaded subfolders to find which root share owns this folder
  for (const share of shares) {
    if (isDescendantOf(folderId, share.folderId, subfolders)) {
      return share;
    }
  }
  return null;
}

export function isDescendantOf(targetId, parentId, subfolders) {
  const children = subfolders[parentId];
  if (!Array.isArray(children)) return false;
  for (const child of children) {
    if (child.id === targetId) return true;
    if (isDescendantOf(targetId, child.id, subfolders)) return true;
  }
  return false;
}
